"use client";
import React, { useRef, useEffect, useState, useMemo, ReactNode } from "react";
import { useFullPageScroll } from "@/hooks/useFullPageScroll";
import DotNavigation from "./DotNavigation";
import { useHeader } from "@/context/HeaderLayoutContext";
import { FullPageContext } from "@/context/FullPageContext";
import FullPageScrollbar from "./FullPageScrollbar";

interface FullPageContainerProps {
  children: ReactNode;
  duration?: number;
}

export default function FullPageContainer({
  children,
  duration = 700,
}: FullPageContainerProps) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const { setHeaderStyle } = useHeader();

  // Memoize sections so the array identity is stable between renders.
  // Without this, `sections` would be a new array each render and
  // the effect that depends on `sections` would run continuously.
  const sections = useMemo(
    () =>
      React.Children.toArray(children).map((child: any) => ({
        element: child,
        label: child.props.label || "",
        hideDotNav: child.props.hideDotNav || false,
        transparent: child.props.transparent || false,
        id: child.props.id,
      })),
    // only recompute if children changes
    [children]
  );

  const sectionsCount = sections.length;
  const { activeIndex, scrollToSection, isDesktop } = useFullPageScroll({
    sectionsCount,
    duration,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sections[activeIndex]) {
      const current = sections[activeIndex];
      setActiveSectionId(current.id);
      window.history.replaceState(null, "", `#${current.id}`);

      //  Tự động set header theo section active
      setHeaderStyle({
        transparent: current.transparent,
      });
    }
  }, [activeIndex, sections, setHeaderStyle]);

  useEffect(() => {
    if (containerRef.current && isDesktop) {
      containerRef.current.style.transform = `translateY(-${
        activeIndex * 100
      }vh)`;
      containerRef.current.style.transition = `transform ${duration}ms ease`;
    }
  }, [activeIndex, duration, isDesktop]);

  return (
    <FullPageContext.Provider
      value={{
        activeIndex,
        scrollToSection,
        sections,
        sectionsCount,
        isFullPage: isDesktop,
      }}
    >
      <div
        style={{
          overflow: isDesktop ? "hidden" : "visible",
          height: isDesktop ? "100vh" : "auto",
          position: "relative",
        }}
      >
        <div
          ref={containerRef}
          style={{
            height: isDesktop ? `${sectionsCount * 100}vh` : "auto",
            width: "100%",
            transition: `transform ${duration}ms cubic-bezier(0.25, 0.1, 0.25, 1.0)`,
            transform: isDesktop
              ? `translateY(-${activeIndex * 100}vh) translateZ(0)`
              : "none",
            willChange: "transform",
          }}
        >
          {sections.map((section, index) =>
            React.cloneElement(section.element, { index, key: section.id })
          )}
        </div>
        {isDesktop && (
          <>
            <DotNavigation visible={!sections[activeIndex]?.hideDotNav} />
            {/* <FullPageScrollbar /> */}
          </>
        )}
      </div>
    </FullPageContext.Provider>
  );
}
